<template lang="pug">
yz-dialog(v-model="showDialog" persistent scrollable max-width="900px")
  yz-card
    yz-dialog-normal-header(title="Update stock" @close="onCloseDialog")
    yz-dialog-normal-content.px-6.py-6
      v-form(ref="form" v-model="isFormValid")
        //- Product info
        .d-flex.align-center.justify-space-between
          .d-flex.align-center
            .yz-heading-8.text-neutral-700 {{ productName }}
            yz-badge.ml-2(x-small color="blue-100" text-color="blue-500" border) HQ
          .yz-regular-body-sm.text-neutral-500 UOM: {{ productUom }}

        //- Stock action and total adjustment row
        v-row.mt-4(no-gutters)
          v-col.pr-3(cols="6")
            .yz-heading-9.text-neutral-700.mb-1 Stock action
            yz-dropdown(v-model="stockAction" placeholder="Select stock action" :items="actions" item-text="text" item-value="value" :rules="rules.not_empty_select")
          v-col.pl-3(cols="6")
            .yz-regular-body-xs.text-neutral-500.text-right.mb-1 {{ stockCalculationText }}
            .total-adjustment-field.d-flex.align-center.justify-space-between
              .yz-regular-body-sm.text-neutral-500 Total adjustment
              .yz-regular-body-sm.text-neutral-700 {{ adjustmentDisplay }}

        //- Stock cost, cost per item, batch number, expiry date row (received only)
        v-row.mt-5(v-if="stockAction === 'received'" no-gutters)
          v-col.pr-2(cols="3")
            .yz-heading-9.text-neutral-700.mb-1 Stock cost
            yz-text-field(v-model="stockCost" :prefix="$currency.symbol" placeholder="0.00" type="number" min="0" :rules="rules.non_negative" size="lg")
            .d-flex.align-center.mt-1(style="gap: 4px;")
              v-icon(size="14" color="neutral500") $yzi-info-circle
              .yz-regular-body-2xs.text-neutral-500 Fill up stock cost to get an accurate cost per item.
          v-col.px-2(cols="3")
            .yz-heading-9.text-neutral-700.mb-1 Cost per item
            yz-text-field(:value="costPerItem" :prefix="$currency.symbol" placeholder="0.00" disabled background-color="neutral200" size="lg")
          v-col.px-2(cols="3")
            .yz-heading-9.text-neutral-700.mb-1 Batch number
            yz-text-field(v-model="stockBatchNo" placeholder="Batch number" size="lg")
          v-col.pl-2(cols="3")
            .yz-heading-9.text-neutral-700.mb-1 Expiry date
            yz-text-field(:value="expiryDateField" placeholder="Expiry date" readonly clickable prepend-inner-icon="$yzi-calendar" clearable size="lg" @click="showExpiryPicker = true" @click:clear="selectedExpiryDate = null")

        //- Expiry date picker dialog (received only)
        yz-dialog(v-if="stockAction === 'received'" v-model="showExpiryPicker" max-width="380px")
          yz-card
            yz-dialog-normal-content.px-2.pt-2.pb-0
              yz-date-picker(v-model="selectedExpiryDate" @change="onPickExpiryDate")

        //- Assigned branches and warehouses
        .mt-5.pt-5(style="border-top: 1px solid #eee;")
          .d-flex.align-center.justify-space-between
            div
              .yz-heading-8.text-neutral-700 Assigned branches and warehouses
              .yz-regular-body-sm.mt-1 Apply stock adjustments to assigned branches or warehouses.
            hq-location-filter(v-model="locationFilter" size="md")

          yz-data-table.mt-3(:headers="tableHeaders" :items="branchLocations" :loading="isFetchingBranches" :server-items-length="totalCount" disable-sort hide-default-footer :items-per-page="locationPageSize" header-class="yz-medium-body-xs")
            template(v-slot:item.name="{ item }")
              .yz-heading-9.text-neutral-700 {{ item.name }}
            template(v-slot:item.adjustment="{ item }")
              yz-text-field(:value="getAdjustment(item.id)" placeholder="0" type="number" min="0" :rules="rules.non_negative" size="sm" :prefix="adjustmentPrefix" :disabled="!stockAction" :background-color="!stockAction ? 'neutral-300' : ''" style="max-width: 120px;" @input="onAdjustmentChange(item, $event)")
            template(v-slot:item.on_hand="{ item }")
              .yz-regular-body-sm.text-neutral-700 {{ item.on_hand }}
            template(v-slot:item.available="{ item }")
              .yz-regular-body-sm.text-neutral-700 {{ availableDisplay(item) }}
            template(v-slot:item.committed="{ item }")
              .yz-regular-body-sm.text-neutral-700 {{ item.committed }}
            template(v-slot:item.reserved="{ item }")
              .yz-regular-body-sm.text-neutral-700 {{ item.reserved }}

          yz-pagination.mt-3(v-if="totalCount > locationPageSize" v-model="locationPage" :page-size.sync="locationPageSize" :count="totalCount" type="item" text="locations" change-page-size arrow)

        //- Remarks
        .mt-6
          .yz-heading-9.text-neutral-700.mb-1 Remarks
          yz-textarea(v-model="remarks" placeholder="Write your remarks here" rows="4")

    yz-dialog-normal-actions(save-label="Save" cancel-label="Cancel" :loading="isLoading" :disable-save="!canSave" @save="onSave" @cancel="onCloseDialog")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import HqLocationFilter from './HqLocationFilter.vue'

export default {
  components: {
    HqLocationFilter
  },
  props: {
    open: { type: Boolean, default: false },
    item: { type: Object, default: null }
  },
  data () {
    return {
      isLoading: false,
      isFetchingBranches: false,
      isFormValid: false,
      stockAction: null,
      stockCost: null,
      stockBatchNo: '',
      selectedExpiryDate: null,
      showExpiryPicker: false,
      remarks: '',
      branchLocations: [],
      locationFilter: null,
      locationPage: 1,
      locationPageSize: 10,
      totalCount: 0,
      adjustmentMap: {},
      metaInventoryQty: 0,
      actions: [
        { text: 'Stock received', value: 'received' },
        { text: 'Inventory re-count', value: 're-count' },
        { text: 'Damage', value: 'damage' },
        { text: 'Theft', value: 'theft' },
        { text: 'Loss', value: 'loss' },
        { text: 'Expired', value: 'expired' },
        { text: 'Stock return', value: 'return' },
        { text: 'Clinic use', value: 'use' }
      ],
      rules: {
        not_empty_select: [v => !!v || 'This field is required'],
        non_negative: [v => v === null || v === '' || Number(v) >= 0 || 'Value cannot be negative']
      }
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get () { return this.open },
      set (newValue) { this.$emit('update:open', newValue) }
    },
    productName () {
      if (!this.item) return ''
      return this.item.product ? this.item.product.name : this.item.name
    },
    productUom () {
      if (!this.item) return '-'
      const product = this.item.product || this.item
      if (product.uom_conversion_enabled && product.uom_conversions?.length > 0) {
        return product.uom_conversions[product.uom_conversions.length - 1].convert_to
      }
      return product.uom || '-'
    },
    currentTotalStock () {
      return this.metaInventoryQty
    },
    totalAdjustment () {
      return Object.values(this.adjustmentMap).reduce((sum, entry) => {
        if (this.stockAction === 're-count') return sum + (Number(entry.adjustment) - (entry.on_hand || 0))
        return sum + (Number(entry.adjustment) || 0)
      }, 0)
    },
    adjustmentPrefix () {
      if (!this.stockAction) return ''
      if (this.stockAction === 're-count') return ''
      if (this.stockAction === 'received') return '+'
      return '-'
    },
    adjustmentDisplay () {
      if (!this.stockAction || this.totalAdjustment === 0) return '0'
      if (this.stockAction === 're-count') {
        const sign = this.totalAdjustment > 0 ? '+' : ''
        return `${sign}${this.totalAdjustment}`
      }
      if (this.adjustmentPrefix) return `${this.adjustmentPrefix} ${this.totalAdjustment}`
      return `${this.totalAdjustment}`
    },
    stockCalculationText () {
      if (!this.stockAction || this.totalAdjustment === 0) return `Current total stock on hand: ${this.currentTotalStock}`
      const current = this.currentTotalStock
      const adj = this.totalAdjustment
      if (this.stockAction === 're-count') {
        const deltas = Object.values(this.adjustmentMap)
          .map(e => e.adjustment - (e.on_hand || 0))
          .filter(d => d !== 0)
          .map(d => d > 0 ? `+${d}` : `${d}`)
          .join(' ')
        return `${current} ${deltas} = ${current + adj}`
      }
      if (this.stockAction === 'received') return `${current}+${adj}=${current + adj}`
      return `${current}-${adj}=${current - adj}`
    },
    costPerItem () {
      const cost = Number(this.stockCost) || 0
      const adj = this.totalAdjustment || 0
      if (cost && adj > 0) return (cost / adj).toFixed(2)
      return '0.00'
    },
    expiryDateField () {
      if (!this.selectedExpiryDate) return ''
      const d = new Date(this.selectedExpiryDate)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    tableHeaders () {
      if (this.stockAction === 're-count') {
        return [
          { text: 'Branches & warehouses', value: 'name', sortable: false },
          { text: 'Current on hand', value: 'on_hand', sortable: false, align: 'left' },
          { text: 'New on hand', value: 'adjustment', sortable: false, width: '140px' },
          { text: 'Available', value: 'available', sortable: false, align: 'left' },
          { text: 'Committed', value: 'committed', sortable: false, align: 'left' },
          { text: 'Reserved', value: 'reserved', sortable: false, align: 'left' }
        ]
      }
      return [
        { text: 'Branches & warehouses', value: 'name', sortable: false },
        { text: 'Adjustment', value: 'adjustment', sortable: false, width: '140px' },
        { text: 'On hand', value: 'on_hand', sortable: false, align: 'left' },
        { text: 'Available', value: 'available', sortable: false, align: 'left' },
        { text: 'Committed', value: 'committed', sortable: false, align: 'left' },
        { text: 'Reserved', value: 'reserved', sortable: false, align: 'left' }
      ]
    },
    canSave () {
      return this.isFormValid && this.stockAction && this.totalAdjustment !== 0 && !this.isLoading
    }
  },
  watch: {
    locationFilter () {
      this.locationPage = 1
      this.fetchAssignedBranches()
    },
    locationPage () {
      this.fetchAssignedBranches()
    },
    locationPageSize () {
      this.locationPage = 1
      this.fetchAssignedBranches()
    },
    stockAction () {
      this.adjustmentMap = {}
    }
  },
  mounted () {
    this.fetchAssignedBranches()
  },
  methods: {
    ...mapActions(['showMessage']),
    availableDisplay (item) {
      if (this.stockAction !== 're-count') return item.available
      const entry = this.adjustmentMap[item.id]
      if (!entry) return item.available
      const delta = entry.adjustment - item.on_hand
      if (delta === 0) return item.available
      const newAvailable = item.available + delta
      const sign = delta > 0 ? '+' : ''
      return `${newAvailable} (${sign}${delta})`
    },
    getAdjustment (id) {
      return this.adjustmentMap[id]?.adjustment ?? null
    },
    onAdjustmentChange (item, value) {
      const num = (value === null || value === '') ? null : Math.max(0, Number(value) || 0)
      if (!num) {
        this.$delete(this.adjustmentMap, item.id)
      } else {
        this.$set(this.adjustmentMap, item.id, {
          adjustment: num,
          productId: item.productId,
          on_hand: item.on_hand
        })
      }
    },
    async fetchAssignedBranches () {
      if (!this.item) return
      this.isFetchingBranches = true
      try {
        const params = { page: this.locationPage, page_size: this.locationPageSize, assigned: true }
        if (this.locationFilter) params.branch_ids = this.locationFilter.join(',')
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/hq/medications/${this.item.id}/branches`, { params })
        this.totalCount = resp.data.count
        this.metaInventoryQty = resp.data.meta?.inventory_qty || 0
        this.branchLocations = (resp.data.results || []).map(b => ({
          id: b.id,
          productId: b.product?.id || b.product_id,
          name: b.name || b.company_name,
          type: b.os === 'warehouse' ? 'warehouse' : 'branch',
          on_hand: b.product?.inventory_qty || 0,
          available: b.product?.available_qty || 0,
          committed: b.product?.committed_qty || 0,
          reserved: b.product?.reserved_qty || 0
        }))
      } catch(error) {
        console.error('Error fetching assigned branches:', error)
        this.showMessage(['error', 'Failed to fetch assigned branches'])
      } finally {
        this.isFetchingBranches = false
      }
    },
    computeBranchChange (adjustment, onHand) {
      const adj = Number(adjustment) || 0
      if (this.stockAction === 're-count') return adj - onHand
      if (this.stockAction === 'received') return adj
      return -adj
    },
    buildPayload () {
      const entries = Object.values(this.adjustmentMap)
      const totalChange = entries.reduce((sum, e) => sum + this.computeBranchChange(e.adjustment, e.on_hand), 0)
      return {
        action: this.stockAction,
        change: totalChange,
        branches: entries.map(e => ({
          product: e.productId,
          change: this.computeBranchChange(e.adjustment, e.on_hand)
        })),
        ...(this.stockCost ? { cost: Number(this.stockCost) } : {}),
        ...(this.stockBatchNo ? { batch_no: this.stockBatchNo } : {}),
        ...(this.selectedExpiryDate ? { expiry_date: this.selectedExpiryDate } : {}),
        ...(this.remarks ? { remarks: this.remarks } : {})
      }
    },
    async onSave () {
      if (!this.$refs.form.validate()) return
      if (!this.canSave) return
      this.isLoading = true
      try {
        const productId = this.item.product ? this.item.product.id : this.item.id
        const payload = this.buildPayload()
        await this.$axios.post(`/products/${productId}/hq/inventories`, payload)
        this.showMessage(['success', 'Stock updated successfully'])
        this.$emit('updated')
        this.onCloseDialog()
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    onPickExpiryDate (date) {
      this.selectedExpiryDate = date
      this.showExpiryPicker = false
    },
    onCloseDialog () {
      this.resetForm()
      this.$emit('update:open', false)
      this.$emit('closed')
    },
    resetForm () {
      this.stockAction = null
      this.stockCost = null
      this.stockBatchNo = ''
      this.selectedExpiryDate = null
      this.showExpiryPicker = false
      this.remarks = ''
      this.branchLocations = []
      this.locationFilter = null
      this.adjustmentMap = {}
      this.metaInventoryQty = 0
      this.totalCount = 0
      this.locationPage = 1
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.w-full {
  width: 100%;
}
.total-adjustment-field {
  background-color: #eee;
  border: 0.75px solid #c1c1c1;
  border-radius: 8px;
  padding: 8px 12px;
  height: 40px;
}
</style>
